import { adaptAnswers } from 'adaptors/adaptAnswers';
import React, {
    useCallback, useState,
} from 'react';

import { InvestorProfileBase } from 'components/InvestorProfileBase/InvestorProfileBase';
import { useInvestorProfile } from 'components/InvestorProfileBase/hooks/useInvestorProfile';
import useLayoutController from 'components/Stepper/RouteBasedStepper/useLayoutController';
import { useChangePlan } from '../../context';
import { Title } from '../../../../../../../../ui-library';
import pencilIcon from '../../../../../../../../clientRequirements/images/pencilIcon.png';
import './InvestorProfile.css';

export const InvestorProfile = ({ dfsClientId, clientAge, clientName }: { dfsClientId: string; clientAge: number; clientName: string }) => {
    const {
        riskProfile: { setData: setRiskProfile },
    } = useChangePlan() as any;

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSending, setSending] = useState(false);
    const {
        isLoading,
        clientId,
        postQuestionnaire,
        handlePostExternalQuestionnaire,
        setChosenRisk,
        calculateRisk,
        allQuestionsFilled,
        errors,
        investorProfileGroup,
        formData,
        groupId,
        data,
        onChange,
        t,
        isEditMode,
        isDataChanged,
        expirationDate,
        hideInvestorProfileEditView,
        showInvestorProfileEditView,
    } = useInvestorProfile({ dfsClientId, clientAge }) as any;

    const onContinue = useCallback(async () => {
        setIsSubmitted(true);
        if (allQuestionsFilled) {
            try {
                setSending(true);
                const answers = adaptAnswers({ data, values: formData, groupId });
                const isQChanged = isDataChanged();

                if (isQChanged) {
                    await postQuestionnaire({ answers });
                    hideInvestorProfileEditView();
                }

                const response = await calculateRisk();

                if (isQChanged) {
                    await setChosenRisk({ id: response?.RiskCategory?.Id, productId: response?.ProductId, submissionId: response?.submissionId });
                }

                await handlePostExternalQuestionnaire();

                setRiskProfile({
                    recommendedStrategy: response,
                    answers,
                });
                setStepComplete(true);
            } catch (err: any) {
                setPageErrors(err?.message || t('contactGroups.somethingWentWrong'));
            } finally {
                setSending(false);
                setIsSubmitted(false);
            }
        }
    }, [allQuestionsFilled, clientId, data, formData, postQuestionnaire, isDataChanged, handlePostExternalQuestionnaire, groupId, calculateRisk, setChosenRisk, setRiskProfile, hideInvestorProfileEditView, t]);

    const { setStepComplete, setPageErrors } = useLayoutController({
        callbackOnContinue: onContinue,
        isContinueDisabled: isSending || isLoading,
        isContinueLoading: isSending,
        isBackDisabled: false,
        reSubscriptionStates: [!!isDataChanged(), allQuestionsFilled],
    });

    return (
        <div className="change-plan investor-profile">
            {!isEditMode
                ? (
                    <>
                        <div className="LinkedHeader">
                            <Title type={3}>
                                {t('portfolio.createNewPortfolio.investorProfile')}
                                {' '}
                                {clientName || ''}
                            </Title>

                            <button
                                data-cy="editInvestorProfile"
                                className="button-link"
                                type="button"
                                onClick={() => {
                                    showInvestorProfileEditView();
                                }}
                            >
                                <span>
                                    <img className="penclIicon" src={pencilIcon} alt="" />
                                </span>
                                {t('interaction.edit')}
                            </button>
                        </div>
                        <p>
                            {t('investorProfile.validTill')}
                            :
                            {' '}
                            {expirationDate}
                        </p>
                    </>
                ) : (
                    <InvestorProfileBase
                        errors={isSubmitted ? errors : {}}
                        isLoading={isLoading || isSending}
                        investorProfileGroup={investorProfileGroup}
                        title={investorProfileGroup?.Name}
                        values={formData?.[groupId] || {}}
                        onChange={onChange}
                        data={investorProfileGroup}
                        t={t}
                    />
                )}
        </div>
    );
};
